import { createContext, type ReactNode, useContext, useMemo } from "react";
import type { CitationGroup } from "./derive-session-context";
import type { SessionContextView } from "./session-context-drawer";
import type { ChatCitations } from "./use-chat-citations";

// Markdown components get only a tag's attributes, so LLMMessages provides the rest for each message.
// Holds only this message's citation group, so a pill re-renders only when that group changes.
type InlineCitationsContextValue = {
  citationGroupId: string;
  citationGroup: CitationGroup | undefined;
  resolveLookedUpCitations: ChatCitations["resolveLookedUpCitations"];
  setView: (view: SessionContextView) => void;
};

const InlineCitationsContext =
  createContext<InlineCitationsContextValue | null>(null);

export const useInlineCitationsContext = () =>
  useContext(InlineCitationsContext);

// Memoized because a fresh value re-renders every inline pill under it.
export const InlineCitationsProvider = ({
  citationGroupId,
  citationGroup,
  resolveLookedUpCitations,
  setView,
  children,
}: Omit<InlineCitationsContextValue, "citationGroupId"> & {
  citationGroupId: string | undefined;
  children: ReactNode;
}) => {
  const value = useMemo(
    () =>
      citationGroupId
        ? {
            citationGroupId,
            citationGroup,
            resolveLookedUpCitations,
            setView,
          }
        : null,
    [citationGroupId, citationGroup, resolveLookedUpCitations, setView],
  );
  return (
    <InlineCitationsContext.Provider value={value}>
      {children}
    </InlineCitationsContext.Provider>
  );
};
